import { AppDispatch, RootState } from "src/Store/store";
import React, { useEffect, useState, useRef, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import PriceRangeSelector from "src/Components/Pharmacy/PriceRangeSelector";
import ProductsViewContainer from "src/Components/Pharmacy/ProductsViewContainer";
import SideCategoryList from "src/Components/Pharmacy/SideCategoryList";
import SideTabList from "src/Components/Pharmacy/SideTabList";
import PharmaCard from "src/Components/WebsiteComponents/HomePage/Cards/PharmaCard";
import {
  convertToApiSortKey,
  convertToKebabCase,
  decodedToken,
  getHeaderHeight,
  parseQueryParams,
} from "src/helpers/helperFunctions";
import httpRequest from "src/helpers/httpRequest";
import { ImageBaseUrl, QueryPath } from "src/lib/constans";
import { categoryList } from "src/lib/DummmyData";
import {
  IPharmacyObj,
  IqueryParams,
  ProductSortType,
} from "src/types/GlobalInterfaces";
import { IPaginated } from "src/types/paginated";
import qs from "qs";
import { useQueryParams } from "src/hooks/useQueryParams";
import { removeAllDisLike } from "src/Store/disLike";

interface IResponse {
  documents: IPharmacyObj[];
  paginated: IPaginated;
}

interface params extends IqueryParams {
  brandId: string[];
  mainCategoryId: string[];
  subCategoryId: string[];
  childCategoryId: string[];
  sortBy: ProductSortType;
}

const ProductsByCategoryPage = () => {
  const { userDetails } = useSelector((store: RootState) => store.appReducer);
  const { pharmacyDisLike } = useSelector((state: RootState) => state.disLike);
  const dispatch = useDispatch<AppDispatch>();
  const [brandCategories, setBrandCategories] = useState<any[]>([]);
  const [brandCategoriesLoading, setBrandCategoriesLoading] =
    useState<boolean>(false);
  const [currentSort, setCurrentSort] = useState<string>("Most Popular");
  const [breadCrumbData, setBreadCrumbData] = useState<any[]>([]);
  const { id } = useParams();
  const searchParams = useQueryParams();
  const tagId = searchParams.getParam(QueryPath.promotionalTag) || "";
  const tagName = searchParams.getParam(QueryPath.promotionalTagName) || "";
  const mainCategoryId = searchParams.getParam(QueryPath.mainCategory) || "";
  const mainCategoryName =
    searchParams.getParam(QueryPath.mainCategoryName) || "";
  const subCategoryId = searchParams.getParam(QueryPath.subCategory) || "";
  const subCategoryName =
    searchParams.getParam(QueryPath.subCategoryName) || "";
  const childCategoryId = searchParams.getParam(QueryPath.childCategory) || "";
  const childCategoryName =
    searchParams.getParam(QueryPath.childCategoryName) || "";
  const brandId = searchParams.getParam(QueryPath.brand) || "";
  const brandName = searchParams.getParam(QueryPath.brandName) || "";
  const search = searchParams.getParam(QueryPath.search) || "";

  const navigate = useNavigate();

  const [isLoader, setIsLoader] = useState<boolean>(false);
  const [paginated, setPaginated] = useState<IPaginated>({
    currentPage: 1,
    totalPages: 1,
  });
  const [nextCursor, setNextCursor] = useState<{
    cursorId?: string;
    cursorPrice?: string;
    cursorKey?: string;
  }>({
    cursorId: "",
    cursorPrice: "",
    cursorKey: "",
  });

  const [products, setProducts] = useState<IPharmacyObj[]>([]);
  const [queryParams, setQueryParams] = useState<params>({
    page: 1,
    limit: 12,
    userId: decodedToken()?.id || "",
    minPrice: 0,
    maxPrice: 100000,
    brandId: [],
    mainCategoryId: [],
    subCategoryId: [],
    childCategoryId: [],
    sortBy: "most-popular",
    promotionalTagId: tagId || "",
    search: search || "",
  });

  const [bannerData, setBannerData] = useState<any>(null);
  const [bannerDataLoading, setBannerDataLoading] = useState<boolean>(false);

  // Debouncing refs
  const debounceTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const searchDebounceTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const lastApiCallRef = useRef<number>(0);
  const debounceDelay = 500; // 500ms delay for filters
  const searchDebounceDelay = 800; // 800ms delay for search
  const getMainBannerData = async (
    path: string,
    type:
      | "main-category"
      | "sub-category"
      | "child-category"
      | "promotional-tag",
    id: string
  ) => {
    if (bannerData?.id === id) {
      return;
    }
    setBannerDataLoading(true);
    const { res, err } = await httpRequest<any>({
      path: path,
    });
    if (res) {
      if (type === "main-category") {
        setBannerData({
          description: res?.description,
          image: res?.categoryBanner,
          logo: res?.categoryImage,
          title: res?.categoryName,
          id: res?._id,
        });
      } else if (type === "sub-category") {
        let d = res?.document;
        setBannerData({
          description: d?.description,
          image: d?.subCategoryBanner,
          logo: d?.subCategoryImage,
          title: d?.subCategoryName,
          id: d?._id,
        });
      } else if (type === "child-category") {
        let d = res?.child[0];
        setBannerData({
          description: d?.description,
          image: d?.categoryBanner,
          logo: d?.childCategoryImage,
          title: d?.childCategoryName,
          id: d?._id,
        });
      } else if (type === "promotional-tag") {
        setBannerData({
          title: res?.data?.name,
          id: res?.data?._id,
        });
      }
    } else {
      toast(err, { type: "error" });
    }
    setBannerDataLoading(false);
  };

  const fetchBrands = async () => {
    setBrandCategoriesLoading(true);
    const { res, err } = await httpRequest<{
      documents: {
        _id: string;
        brandName: string;
        brandLogo: string;
        [key: string]: any;
      }[];
    }>({ path: "/brand/home/getallbrands" });
    if (res) {
      const _data = res?.documents || [];
      setBrandCategories((prev) =>
        _data.map((item) => ({
          id: item._id,
          label: item.brandName,
        }))
      );
    } else {
      toast(err, { type: "error" });
    }
    setBrandCategoriesLoading(false);
  };

  useEffect(() => {
    fetchBrands();
  }, []);

  // Cleanup timeout on component unmount
  useEffect(() => {
    return () => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
      if (searchDebounceTimeoutRef.current) {
        clearTimeout(searchDebounceTimeoutRef.current);
      }
    };
  }, []);

  // Separate function for search with different debouncing
  const handleSearchChange = useCallback((searchValue: string) => {
    handleQueryChange(
      { search: searchValue },
      true,
      true,
      true // isSearch = true
    );
  }, []);

  // Debounced version of fetchAllProducts for filters
  const debouncedFetchProducts = useCallback((params: any) => {
    // Clear existing timeout
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
    }
    
    // Set new timeout
    debounceTimeoutRef.current = setTimeout(() => {
      console.log("Debounced API call for filters:", params);
      fetchAllProducts(params, false); // false = replace data, not append
    }, debounceDelay);
  }, []);

  // Debounced version for search
  const debouncedSearchProducts = useCallback((params: any) => {
    // Clear existing timeout
    if (searchDebounceTimeoutRef.current) {
      clearTimeout(searchDebounceTimeoutRef.current);
    }
    
    // Set new timeout
    searchDebounceTimeoutRef.current = setTimeout(() => {
      console.log("Debounced API call for search:", params);
      fetchAllProducts(params, false); // false = replace data, not append
    }, searchDebounceDelay);
  }, []);

  const fetchAllProducts = async (q: any, isLoadMore = false) => {
    let newq = {
      ...q,
    };

    if (
      q.sortBy === "lowToHigh" ||
      (q.sortBy === "highToLow" && newq.page > 1)
    ) {
      newq = {
        ...newq,
        ...nextCursor,
      };
    }

    setIsLoader(true);
    const { res, err } = await httpRequest<IResponse>({
      path: `/pharma-inventory/home/getPharmaProducts?${qs?.stringify(newq)}`,
    });
    if (res) {
      let { documents = [], paginated } = res;
      
      // Only append for load more, replace for new searches
      if (isLoadMore) {
        setProducts((prev) => {
          // Prevent duplicates by checking existing IDs
          const existingIds = new Set(prev.map(product => product._id));
          const newData = documents.filter(product => !existingIds.has(product._id));
          return [...prev, ...newData];
        });
      } else {
        setProducts(documents);
      }
      
      setPaginated(paginated);
      dispatch(removeAllDisLike("pharmacyDisLike"));
      setNextCursor(paginated?.nextCursor || {});
    } else {
      toast(err, { type: "error" });
    }
    setIsLoader(false);
  };

  const handleQueryChange = (
    data: Partial<params>,
    update: boolean = true,
    reset: boolean = true,
    isSearch: boolean = false
  ) => {
    let qP = {
      ...queryParams,
      ...data,
    };
    setQueryParams(qP);
    if (reset) {
      setProducts([]);
      qP.page = 1;
      window.scrollTo(0, 0);
    }
    if (update) {
      const params = {
        ...qP,
        sortBy: convertToApiSortKey(qP?.sortBy || "most-popular"),
      };
      
      // Use different debouncing for search vs filters
      if (isSearch) {
        debouncedSearchProducts(params);
      } else {
        debouncedFetchProducts(params);
      }
    }
  };

  useEffect(() => {
    let breadCrumbData = [];
    if (
      mainCategoryId ||
      subCategoryId ||
      childCategoryId ||
      tagId ||
      brandId
    ) {
      console.log(
        "Main Category",
        mainCategoryId,
        subCategoryId,
        childCategoryId
      );
      const data = [
        {
          label: "Home",
          onClick: () => navigate("/"),
        },
        {
          label: mainCategoryName,
          onClick: () =>
            navigate(
              `/category/${convertToKebabCase(
                mainCategoryName
              )}?${parseQueryParams({
                [QueryPath.mainCategory]: mainCategoryId,
                [QueryPath.mainCategoryName]: mainCategoryName,
              })}`
            ),
        },
        {
          label: subCategoryName,
          onClick: () =>
            navigate(
              `/category/${convertToKebabCase(
                mainCategoryName
              )}/${convertToKebabCase(subCategoryName)}?${parseQueryParams({
                [QueryPath.mainCategory]: mainCategoryId,
                [QueryPath.mainCategoryName]: mainCategoryName,
                [QueryPath.subCategory]: subCategoryId,
                [QueryPath.subCategoryName]: subCategoryName,
              })}`
            ),
        },
        {
          label: childCategoryName,
          onClick: () =>
            navigate(
              `/category/${convertToKebabCase(
                mainCategoryName
              )}/${convertToKebabCase(subCategoryName)}/${convertToKebabCase(
                childCategoryName
              )}?${parseQueryParams({
                [QueryPath.mainCategory]: mainCategoryId,
                [QueryPath.mainCategoryName]: mainCategoryName,
                [QueryPath.subCategory]: subCategoryId,
                [QueryPath.subCategoryName]: subCategoryName,
                [QueryPath.childCategory]: childCategoryId,
                [QueryPath.childCategoryName]: childCategoryName,
              })}`
            ),
        },
        {
          label: tagName,
        },
      ];
      if (childCategoryId) {
        getMainBannerData(
          `/child-category/by-category/${childCategoryId}`,
          "child-category",
          childCategoryId
        );
      } else if (subCategoryId) {
        getMainBannerData(
          `/sub-category/${subCategoryId}`,
          "sub-category",
          subCategoryId
        );
      } else if (tagId) {
        getMainBannerData(
          `/tags/promotional/${tagId}`,
          "promotional-tag",
          tagId
        );
      } else if (mainCategoryId) {
        getMainBannerData(
          `/main-category/${mainCategoryId}`,
          "main-category",
          mainCategoryId
        );
      } else {
      }

      breadCrumbData = data.filter((item) => item.label);

      handleQueryChange({
        mainCategoryId: mainCategoryId ? [mainCategoryId] : [],
        subCategoryId: subCategoryId ? [subCategoryId] : [],
        childCategoryId: childCategoryId ? [childCategoryId] : [],
        promotionalTagId: tagId || "",
        brandId: brandId ? brandId.split(",") : [],
        search: search || "",
      });
    } else {
      breadCrumbData = [
        {
          label: "Home",
          onClick: () => navigate("/"),
        },
        {
          label: "All Products",
        },
        {
          label: search || "",
        },
      ];
      handleQueryChange({
        page: 1,
        brandId: [],
        mainCategoryId: [],
        subCategoryId: [],
        childCategoryId: [],
        search: search || "",
      });
    }
    if(breadCrumbData.length <= 1) {
      breadCrumbData = [
        {
          label: "Home",
          onClick: () => navigate("/"),
        },
        {
          label: "All Products",
        },
        {
          label: search || "",
        },
      ]
    }
    setBreadCrumbData(breadCrumbData.filter((item) => item.label));
  }, [
    mainCategoryId,
    subCategoryId,
    childCategoryId,
    tagId,
    mainCategoryName,
    subCategoryName,
    childCategoryName,
    tagName,
    brandId,
    brandName,
    search,
  ]);

  useEffect(() => {
    if (pharmacyDisLike.length > 0) {
      console.log("pharmacyDisLike", pharmacyDisLike);
      let updatedStatusData = products.map((v) => ({
        ...v,
        isLiked: pharmacyDisLike.find((i) => i === v?._id) ? false : v?.isLiked,
      }));
      console.log("updatedStatusData", updatedStatusData);
      setProducts(updatedStatusData);
      dispatch(removeAllDisLike("pharmacyDisLike"));
    }
  }, [pharmacyDisLike]);

  return (
    <div className="w-full">
      <div className="view-area-width flex gap-1 py-3 relative">
        <div
          className="p-[2px] product-side-bar-width custom-ai-scroll-bar !sticky overflow-y-auto"
          style={{
            height: `calc(100vh - ${getHeaderHeight()}px - 16px)`,
            top: `${getHeaderHeight() + 12}px`,
          }}
        >
          <div className="w-full box-design bg-white">
            <SideTabList
              title="Brands"
              List={brandCategories}
              onListClick={(item) => {
                let find = queryParams?.brandId.find((id) => id === item.id);
                let ids = queryParams?.brandId;
                console.log("Ids Before Logic ===> ", ids);
                if (find) {
                  ids = ids.filter((id) => id !== item.id);
                } else {
                  ids.push(item.id);
                }
                console.log("Ids After Logic ===> ", ids);
                let q = {
                  [QueryPath.brand]: ids,
                  [QueryPath.brandName]: ids,
                  [QueryPath.mainCategory]: mainCategoryId || "",
                  [QueryPath.mainCategoryName]: mainCategoryName || "",
                  [QueryPath.subCategory]: subCategoryId || "",
                  [QueryPath.subCategoryName]: subCategoryName || "",
                  [QueryPath.childCategory]: childCategoryId || "",
                  [QueryPath.childCategoryName]: childCategoryName || "",
                  // [QueryPath.promotionalTag]: tagId || "",
                  // [QueryPath.promotionalTagName]: tagName || "",
                  [QueryPath.search]: search || "",
                };
                console.log("Query Object Before Navigate ===> ", q);
                navigate(`?${parseQueryParams(q)}`);
              }}
              showCheckBox={true}
              showRadio={false}
              loading={brandCategoriesLoading}
              loadingText="Loading Brand Categories"
              selectedItem={queryParams?.brandId}
            />
            <PriceRangeSelector
              min={queryParams?.minPrice}
              max={queryParams?.maxPrice}
              onChange={(min, max) => {
                handleQueryChange({ minPrice: min, maxPrice: max });
              }}
            />
            <SideCategoryList
              title="Categories"
              List={categoryList}
              borderBottom={false}
            />
          </div>
        </div>
        <ProductsViewContainer
          breadCrumbData={breadCrumbData}
          bannerData={bannerData}
          bannerLoading={bannerDataLoading}
          currentPage={queryParams?.page}
          totalPages={paginated?.totalPages}
          onLoadAgain={() => {
            handleQueryChange({ page: 1 });
          }}
          onLoadMore={(page) => {
            console.log("Load More", page);
            // For load more, we need to append data, not replace
            let updatedParams = { ...queryParams, page: page };
            setQueryParams(updatedParams);
            fetchAllProducts({
              ...updatedParams,
              sortBy: convertToApiSortKey(updatedParams?.sortBy || "most-popular"),
            }, true); // true = append data
          }}
          sortOptions={[
            {
              label: "Most Popular",
              value: "most-popular",
            },
            {
              label: "Most Rated",
              value: "most-rated",
            },
            {
              label: "Low to High",
              value: "price-low-to-high",
            },
            {
              label: "High to Low",
              value: "price-high-to-low",
            },
          ]}
          currentSort={queryParams?.sortBy}
          onSortChange={(sortBy) => {
            handleQueryChange({ sortBy }, true);
          }}
          products={products}
          loading={isLoader}
          renderProduct={(product, index) => (
            <PharmaCard
              key={product?.id}
              className="w-full !px-0 !pb-0"
              cardClassName="w-full"
              product={product}
              index={index}
              onLikeProduct={(id, status) => {
                setProducts((prev) =>
                  prev?.map((item) => {
                    if (item?._id === id) {
                      return {
                        ...item,
                        isLiked: status,
                      };
                    }
                    return item;
                  })
                );
              }}
            />
          )}
          renderSkeleton={() => (
            <PharmaCard
              className="w-full !px-0 !pb-0"
              cardClassName="w-full"
              key={"sdsd"}
              skeleton
            />
          )}
        />
      </div>
    </div>
  );
};

export default ProductsByCategoryPage;
